// Corresponds to backend/logic/user_logic.py Pydantic models
import apiClient from './api';

// Base URL is no longer needed as apiClient sets it
// const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

export enum PositionEnum {
  ACADEMIA = "academia",
  DATA_SCIENCE = "data_science",
  ENGINEER = "engineer",
}

// Interface for user data returned by the API (matches User Pydantic model)
export interface User {
  id: number;
  email: string;
  name: string;
  class_year?: number | null;
  position?: PositionEnum | null;
  avatar_url?: string | null;
  elo: number;
  is_admin: boolean;
  is_fake: boolean;
  voter_trust_alpha: number;
  voter_trust_beta: number;
  deleted_at?: string | null; // Represent datetimes as strings
  created_at: string;
  updated_at: string;
}

// Interface for data needed to create a user (matches UserCreate Pydantic model)
export interface UserCreate {
  email: string;
  name: string;
  linkedin_id?: string | null;
  class_year?: number | null;
  position?: PositionEnum | null;
  avatar_url?: string | null;
}

/**
 * Fetches a user by their ID from the backend.
 * @param userId - The ID of the user to fetch.
 * @returns A promise that resolves to the User object.
 * @throws Throws an error if the fetch fails or the user is not found.
 */
export async function getUser(userId: number): Promise<User> {
  try {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      throw new Error(`User with ID ${userId} not found`);
    }
    throw new Error(`Failed to fetch user ${userId}: ${error.message}`);
  }
}

/**
 * Fetches a user by their email address from the backend.
 * Returns the user if found, or null if not found (404).
 * @param email - The email of the user to fetch.
 * @returns A promise that resolves to the User object or null.
 * @throws Throws an error for non-404 fetch failures.
 */
export async function getUserByEmail(email: string): Promise<User | null> {
  const encodedEmail = encodeURIComponent(email);
  try {
    const response = await apiClient.get(`/users/by-email/${encodedEmail}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return null; // Explicitly return null for not found
    }
    throw new Error(`Failed to fetch user by email ${email}: ${error.message}`);
  }
}

/**
 * Sends a request to create a new user to the backend.
 * @param userData - The data for the new user.
 * @returns A promise that resolves to the created User object.
 * @throws Throws an error if the creation fails (e.g., validation error, duplicate email).
 */
export async function createUser(userData: UserCreate): Promise<User> {
  try {
    const response = await apiClient.post('/users/', userData);
    return response.data;
  } catch (error: any) {
    throw new Error(`Failed to create user: ${error.message}`);
  }
}

/**
 * Fetches a list of users, optionally filtering by email.
 * @param filters - An object containing filters (e.g., { email: 'test@example.com' }).
 * @returns A promise that resolves to an array of User objects.
 * @throws Throws an error if the fetch fails.
 */
export async function listUsers(
  filters: { email?: string; limit?: number; } = {}
): Promise<User[]> {
  try {
    const response = await apiClient.get('/users/', { params: filters });
    return response.data;
  } catch (error: any) {
    throw new Error(`Failed to list users: ${error.message}`);
  }
}

/**
 * Sends a request to hard delete a user by their ID from the backend.
 * @param userId - The ID of the user to hard delete.
 * @returns A promise that resolves if the deletion was successful.
 * @throws Throws an error if the deletion fails.
 */
export async function hardDeleteUser(userId: number): Promise<void> {
  try {
    await apiClient.delete(`/users/${userId}/hard`);
  } catch (error: any) {
    throw new Error(`Failed to hard delete user ${userId}: ${error.message}`);
  }
}
